<template>
    <div class="py-4 container-fluid">
        <div class="row">
            <div class="col-12">
                <soft-alert
                    v-if="alertTable.state"
                    :color="alertTable.color"
                    class="mb-3"
                    dismissible
                >
                    <span class="text-sm">{{ alertTable.message }}</span>
                </soft-alert>
            </div>

            <div v-if="sinAcceso" class="col-12">
                <div class="sin-acceso text-center">
                    <i class="fas fa-lock mb-3"></i>
                    <h5 class="font-weight-bolder mb-2">Sin acceso a la auditoría</h5>
                    <p class="text-sm text-secondary mb-0">
                        Solo el administrador principal de la plataforma puede ver este registro.
                        El intento quedó registrado.
                    </p>
                </div>
            </div>

            <div v-else class="col-12">
                <table-component
                    :headers="headers"
                    :items="auditoria.data"
                    :spinner="spinner"
                    name="Auditoría"
                >
                    <template #headers>
                        <filters-auditoria
                            v-model:filtros="filtros"
                            :admins="admins"
                            :tablas="tablas"
                            @search="applyFilters"
                            @clear="cleanFilter"
                            @refresh="getAuditoria(auditoria.current_page || 1)"
                        >
                            <div class="d-flex gap-2 flex-wrap align-items-center ms-auto">
                                <soft-button
                                    variant="outline"
                                    color="primary"
                                    size="sm"
                                    :loading="exportando"
                                    @click="exportar"
                                >
                                    <i class="fas fa-file-csv me-1"></i>
                                    Exportar CSV
                                </soft-button>

                                <span class="badge bg-light text-dark border px-3 py-2">
                                    <i class="fas fa-clipboard-list me-1 text-secondary"></i>
                                    Total: {{ auditoria.total || 0 }}
                                </span>
                            </div>
                        </filters-auditoria>
                        <p class="text-xxs text-muted mb-0">
                            <i class="fas fa-info-circle me-1"></i>
                            Se registran los cambios (crear, editar, eliminar) y las exportaciones de los admins
                            de la plataforma desde que se activó la auditoría. Las consultas no se registran.
                        </p>
                    </template>

                    <template #columnRender="{ header, value }">
                        <span v-if="header.field === 'created_at'" class="text-xs text-nowrap">
                            {{ formatFecha(value.created_at) }}
                        </span>

                        <span v-else-if="header.field === 'admin'" class="d-block">
                            <span class="text-sm fw-semibold text-dark">{{ value.admin_email || `#${value.admin_user_id}` }}</span>
                            <span v-if="value.admin_role_nombre" class="d-block text-xxs text-muted">
                                {{ value.admin_role_nombre }}
                            </span>
                        </span>

                        <span v-else-if="header.field === 'impersonado'">
                            <span v-if="value.impersonado_user_id" class="text-xs">
                                <i class="fas fa-user-secret text-warning me-1" title="Impersonando"></i>
                                {{ value.impersonado_email || `#${value.impersonado_user_id}` }}
                            </span>
                            <span v-else class="text-xxs text-muted">Cuenta propia</span>
                        </span>

                        <span v-else-if="header.field === 'accion'" class="d-block accion-celda">
                            <span class="badge me-1" :class="claseMetodo(value.metodo)">{{ value.metodo }}</span>
                            <code class="text-xs" :title="value.url">{{ value.url }}</code>
                            <span v-if="value.evento" class="d-block text-xxs text-danger fw-semibold">
                                {{ value.evento }}
                            </span>
                        </span>

                        <span v-else-if="header.field === 'status_http'" class="d-block resultado-celda">
                            <span class="badge" :class="value.exito ? 'bg-success' : 'bg-danger'">
                                {{ value.status_http }}
                            </span>
                            <span v-if="value.mensaje_respuesta" class="d-block text-xxs text-muted text-truncate" :title="value.mensaje_respuesta">
                                {{ value.mensaje_respuesta }}
                            </span>
                        </span>

                        <span v-else-if="header.field === 'total_cambios'">
                            <a
                                v-if="value.total_cambios"
                                href="#"
                                class="badge bg-light text-dark border text-decoration-none"
                                title="Ver cambios"
                                @click.prevent="verDetalle(value)"
                            >
                                <i class="fas fa-exchange-alt me-1 text-secondary"></i>
                                {{ value.total_cambios }}
                            </a>
                            <span v-else class="text-xxs text-muted">—</span>
                        </span>

                        <span v-else-if="header.field === 'duracion_ms'" class="text-xs text-muted text-nowrap">
                            {{ value.duracion_ms != null ? `${value.duracion_ms} ms` : '—' }}
                        </span>

                        <span v-else-if="header.field === 'actions'">
                            <a
                                href="#"
                                class="table-action-link"
                                title="Ver detalle"
                                @click.prevent="verDetalle(value)"
                            >
                                <i class="fas fa-eye"></i>
                            </a>
                        </span>

                        <span v-else>{{ value[header.field] }}</span>
                    </template>

                    <template #pagination>
                        <div class="d-flex justify-content-center mt-3">
                            <base-pagination
                                :current-page="auditoria.current_page || 1"
                                :pageCount="auditoria.last_page || 1"
                                @nextPage="pageChangeHandle('next')"
                                @previousPage="pageChangeHandle('previous')"
                                @loadPage="pageChangeHandle"
                            />
                        </div>
                    </template>
                </table-component>

                <soft-modal
                    v-if="modalDetalle && detalle"
                    :modalActive="modalDetalle"
                    size="lg"
                    @close="cerrarDetalle"
                >
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-clipboard-list me-2 text-primary"></i>
                            Registro #{{ detalle.id }}
                        </h5>
                    </template>
                    <detalle-auditoria :registro="detalle" :cargando="cargandoDetalle" @historial="verHistorial" />
                </soft-modal>

                <!-- Va después del detalle para quedar encima de él -->
                <soft-modal
                    v-if="modalHistorial && historial"
                    :modalActive="modalHistorial"
                    size="lg"
                    @close="cerrarHistorial"
                >
                    <template #header>
                        <h5 class="modal-title mb-0">
                            <i class="fas fa-history me-2 text-primary"></i>
                            Historial de {{ historial.tabla }} #{{ historial.registro_id }}
                        </h5>
                    </template>
                    <historial-registro
                        :historial="historial"
                        :cargando="cargandoHistorial"
                        @page="verHistorial(historial, $event)"
                        @filtrar="filtrarPorRegistro"
                    />
                </soft-modal>
            </div>
        </div>
    </div>
</template>

<script>
import tableComponent from '@/components/tableComponent.vue';
import SoftModal from '@/components/SoftModal.vue';
import SoftButton from '@/components/SoftButton.vue';
import SoftAlert from '@/components/SoftAlert.vue';
import BasePagination from '@/components/pagination/BasePagination.vue';
import FiltersAuditoria from './components/filtersAuditoria.vue';
import DetalleAuditoria from './components/detalleAuditoria.vue';
import HistorialRegistro from './components/historialRegistro.vue';
import { useAuditoria } from './useAuditoria.js';
import { formatFecha, claseMetodo } from './formatoAuditoria.js';

export default {
    name: 'auditoriaPlataforma',
    components: {
        tableComponent,
        SoftModal,
        SoftButton,
        SoftAlert,
        BasePagination,
        FiltersAuditoria,
        DetalleAuditoria,
        HistorialRegistro
    },
    setup() {
        const auditoria = useAuditoria();

        auditoria.iniciar();

        return { ...auditoria, formatFecha, claseMetodo };
    }
};
</script>

<style lang="scss" scoped>
.accion-celda code {
    display: inline-block;
    max-width: 280px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: middle;
}

.resultado-celda .text-truncate {
    max-width: 200px;
}

.sin-acceso {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 3rem 1.5rem;

    > i {
        font-size: 2rem;
        color: #cbd5e1;
    }
}

.table-action-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 0.375rem;
    color: #8392ab;
    transition: all 0.15s ease;
    text-decoration: none;

    &:hover {
        background-color: #f0f2f5;
        color: #344767;
    }
}

.modal-title {
    color: #344767;
    font-weight: 600;
}
</style>
